// src/pages/LancamentoVisita.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, Navigate } from 'react-router-dom'; // <-- Adicione o Navigate aqui
import { Search, PlusCircle, Trash2, Home, Download, Zap, ChevronDown, ChevronUp,
         CheckCircle2, XCircle, Ban, Calendar, Clock, CopyCheck } from 'lucide-react';
import { apiFetch } from '../utils/apiFetch';

// --- FONT INJECTION ---
const fontStyle = document.createElement('style');
fontStyle.textContent = `@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');`;
document.head.appendChild(fontStyle);

// --- CSS GLOBAL ---
const globalStyle = document.createElement('style');
globalStyle.textContent = `
  * { box-sizing: border-box; }
  .acs-app { font-family: 'Plus Jakarta Sans', sans-serif; }
  .member-row { transition: background 0.15s ease; }
  .member-row:hover { background: #f8fafc !important; }
  .pill-btn { transition: all 0.12s ease; }
  .pill-btn:hover:not(:disabled) { transform: translateY(-1px); }
  .house-card { transition: box-shadow 0.2s ease; }
  .house-card:hover { box-shadow: 0 4px 24px rgba(0,0,0,0.08) !important; }
  .search-result-item { transition: background 0.12s; }
  .search-result-item:hover { background: #f0fdf4 !important; }
  .export-btn { transition: all 0.15s ease; }
  .export-btn:hover { transform: translateY(-1px); box-shadow: 0 4px 16px rgba(5, 150, 105, 0.35) !important; }
  .status-btn { transition: all 0.12s ease; }
  .status-btn:hover:not(:disabled) { transform: scale(1.08); }
  ::-webkit-scrollbar { width: 6px; } ::-webkit-scrollbar-track { background: #f1f5f9; } ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
  .member-name-text { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

  /* ── SmartDatePicker ── */
  .sdp-wrap { position: relative; display: inline-flex; align-items: center; }
  .sdp-text {
    padding: 5px 28px 5px 9px;
    border-radius: 6px; border: 1px solid #e2e8f0;
    background: white; font-size: 0.75rem; color: #334155;
    font-family: inherit; font-weight: 600; outline: none;
    width: 108px; transition: border-color 0.15s;
    letter-spacing: 0.01em;
  }
  .sdp-text:focus { border-color: #059669; box-shadow: 0 0 0 3px rgba(5,150,105,0.10); }
  .sdp-text.invalid { border-color: #fca5a5; color: #dc2626; }
  .sdp-text.valid { border-color: #6ee7b7; }
  .sdp-cal-btn {
    position: absolute; right: 0; top: 0; bottom: 0;
    width: 26px; display: flex; align-items: center; justify-content: center;
    background: transparent; border: none; cursor: pointer;
    color: #94a3b8; border-radius: 0 6px 6px 0;
    transition: color 0.15s;
  }
  .sdp-cal-btn:hover { color: #059669; }
  .sdp-hidden { position: absolute; opacity: 0; width: 0; height: 0; pointer-events: none; }
`;
document.head.appendChild(globalStyle);

// ─── SMART DATE PICKER ────────────────────────────────────────────────────────
// Aceita digitação livre (dd/mm/aaaa, aaaa-mm-dd, colar) e ícone de calendário.
// value / onChange são sempre no formato ISO "yyyy-mm-dd" (igual ao input[type=date]).
const SmartDatePicker = ({ value, onChange, style = {} }) => {
    // Converte ISO → display "dd/mm/aaaa"
    const isoToDisplay = (iso) => {
        if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso || '';
        const [y, m, d] = iso.split('-');
        return `${d}/${m}/${y}`;
    };

    const [text, setText] = React.useState(() => isoToDisplay(value));
    const [valid, setValid] = React.useState(true);
    const hiddenRef = React.useRef(null);

    // Sincroniza quando value muda externamente
    React.useEffect(() => {
        setText(isoToDisplay(value));
        setValid(true);
    }, [value]);

    // Tenta parsear qualquer formato razoável → ISO "yyyy-mm-dd" ou null
    const parseToISO = (raw) => {
        const s = raw.trim();
        // dd/mm/aaaa
        let m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (m) return `${m[3]}-${m[2].padStart(2,'0')}-${m[1].padStart(2,'0')}`;
        // dd-mm-aaaa
        m = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
        if (m) return `${m[3]}-${m[2].padStart(2,'0')}-${m[1].padStart(2,'0')}`;
        // aaaa-mm-dd
        m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (m) return s;
        // aaaa/mm/dd
        m = s.match(/^(\d{4})\/(\d{2})\/(\d{2})$/);
        if (m) return `${m[1]}-${m[2]}-${m[3]}`;
        // ddmmaaaa (8 dígitos)
        m = s.match(/^(\d{2})(\d{2})(\d{4})$/);
        if (m) return `${m[3]}-${m[2]}-${m[1]}`;
        return null;
    };

    const handleBlur = () => {
        const iso = parseToISO(text);
        if (iso) {
            setValid(true);
            setText(isoToDisplay(iso));
            if (iso !== value) onChange(iso);
        } else if (text === '') {
            setValid(true);
            onChange('');
        } else {
            setValid(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') e.target.blur();
    };

    const openNativePicker = () => {
        if (hiddenRef.current) {
            hiddenRef.current.value = value || '';
            hiddenRef.current.showPicker?.();
            hiddenRef.current.focus();
            hiddenRef.current.click();
        }
    };

    return (
        <div className="sdp-wrap" style={style}>
            <input
                className={`sdp-text ${text && !valid ? 'invalid' : text && valid ? 'valid' : ''}`}
                value={text}
                onChange={(e) => { setText(e.target.value); setValid(true); }}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                placeholder="dd/mm/aaaa"
                title="Digite ou cole a data. Formatos aceitos: dd/mm/aaaa, aaaa-mm-dd, ddmmaaaa"
            />
            <button className="sdp-cal-btn" onClick={openNativePicker} type="button" title="Abrir calendário">
                <Calendar size={13} />
            </button>
            {/* Input nativo escondido apenas para abrir o calendário do sistema */}
            <input
                ref={hiddenRef}
                type="date"
                className="sdp-hidden"
                defaultValue={value}
                onChange={(e) => {
                    const iso = e.target.value;
                    if (iso) { onChange(iso); setText(isoToDisplay(iso)); setValid(true); }
                }}
            />
        </div>
    );
};

// ─── MAPEAMENTOS PARA CSV (compatível com index.js) ───────────────────────────
// IndTurno: 1=Manhã  2=Tarde  3=Noite
const TURNO_MAP = { 'Manhã': 1, 'Tarde': 2, 'Noite': 3 };
// IndDesfecho: 1=Realizada  2=Ausente  3=Recusa
const DESFECHO_MAP = { 'Realizada': 1, 'Ausente': 3, 'Recusa': 2 };

// ─── MAPA ÚNICO: Label → CodMotivoVisitaDomiciliar ────────────────────────────
// Cobre tanto ACOES_DINAMICAS quanto CONDICOES_FIXAS.
// Todos os campos selecionados (ações + condições) viram a coluna MOTIVOS no CSV.
// 'Visita compartilhada' é exceção: vai para a coluna COMPARTILHADA (bool).
const MOTIVO_CODES = {
    // ── Ações de visita ──────────────────────────────────
    'Cadastramento':                                          1,
    'Periódica':                                             2,
    'Consulta':                                              4,
    'Exame':                                                 5,
    'Vacina':                                                6,
    'Bolsa':                                                 7,   // "Bolsa Família" (ação)
    'Gestante':                                              9,   // também condição
    'Puérpera':                                             10,   // também condição
    'Egresso de internação':                                29,
    'Convite atividades coletivas / Campanha de saúde':     31,
    'Orientação / Prevenção':                               32,
    'Outros':                                               33,
    'Ação educativa':                                       36,
    'Imóvel com foco':                                      37,
    'Ação mecânica':                                        38,
    'Tratamento focal':                                     39,
    'Idoso':                                                42,   // também condição
    // ── Condições de saúde (condicoes_fixas) ─────────────
    'Recém-nascido':                                        11,
    'Criança':                                              12,
    'Pessoa com desnutrição':                               13,
    'Pessoa em reabilitação ou com deficiência':            14,
    'Pessoa com hipertensão':                               15,
    'Pessoa com diabetes':                                  16,
    'Pessoa com asma':                                      17,
    'Pessoa com DPOC/Enfisema':                             18,
    'Pessoa com câncer':                                    19,
    'Pessoa com outras doenças crônicas':                   20,
    'Pessoa com hanseníase':                                21,
    'Pessoa com tuberculose':                               22,
    'Domiciliados / Acamados':                              23,
    'Condições de vulnerabilidade social':                  24,
    'Condicionalidade do Bolsa Família':                    25,
    'Saúde mental':                                         26,
    'Usuário de álcool':                                    27,
    'Usuário de outras drogas':                             28,
    'Sintomáticos Respiratórios':                           34,
    'Tabagista':                                            35,
    // 'Visita compartilhada' → coluna COMPARTILHADA (bool), não entra em MOTIVOS
};

// --- DICIONÁRIO AÇÕES ---
const ACOES_DINAMICAS = [
    { id: 'Visita compartilhada',    label: 'Compartilhada', color: '#64748b', bg: '#f1f5f9', activeBorder: '#94a3b8' },
    { id: 'Cadastramento',           label: 'Cadastro',      color: '#0369a1', bg: '#e0f2fe', activeBorder: '#38bdf8' },
    { id: 'Periódica',               label: 'Periódica',     color: '#0369a1', bg: '#e0f2fe', activeBorder: '#38bdf8' },
    { id: 'Consulta',                label: 'Consulta',      color: '#15803d', bg: '#dcfce7', activeBorder: '#4ade80' },
    { id: 'Exame',                   label: 'Exame',         color: '#15803d', bg: '#dcfce7', activeBorder: '#4ade80' },
    { id: 'Vacina',                  label: 'Vacina',        color: '#15803d', bg: '#dcfce7', activeBorder: '#4ade80' },
    { id: 'Bolsa',                   label: 'Bolsa',         color: '#15803d', bg: '#dcfce7', activeBorder: '#4ade80' },
    { id: 'Puérpera',                label: 'Puérpera',      color: '#15803d', bg: '#dcfce7', activeBorder: '#4ade80' },
    { id: 'Ação educativa',          label: 'Educativa',     color: '#6d28d9', bg: '#ede9fe', activeBorder: '#a78bfa' },
    { id: 'Orientação / Prevenção',  label: 'Orientação',    color: '#6d28d9', bg: '#ede9fe', activeBorder: '#a78bfa' },
    { id: 'Convite atividades coletivas / Campanha de saúde', label: 'Convite', color: '#6d28d9', bg: '#ede9fe', activeBorder: '#a78bfa' },
    { id: 'Egresso de internação',   label: 'Egresso',       color: '#6d28d9', bg: '#ede9fe', activeBorder: '#a78bfa' },
    { id: 'Imóvel com foco',         label: 'Foco',          color: '#b45309', bg: '#fef3c7', activeBorder: '#fbbf24' },
    { id: 'Ação mecânica',           label: 'Mecânica',      color: '#b45309', bg: '#fef3c7', activeBorder: '#fbbf24' },
    { id: 'Tratamento focal',        label: 'Focal',         color: '#b45309', bg: '#fef3c7', activeBorder: '#fbbf24' },
    { id: 'Outros',                  label: 'Outros',        color: '#334155', bg: '#e2e8f0', activeBorder: '#94a3b8' },
];

const CONDICOES_FIXAS = [
    'Gestante','Puérpera','Recém-nascido','Criança','Idoso','Pessoa com desnutrição',
    'Pessoa em reabilitação ou com deficiência','Pessoa com hipertensão','Pessoa com diabetes',
    'Pessoa com asma','Pessoa com DPOC/Enfisema','Pessoa com câncer','Pessoa com outras doenças crônicas',
    'Pessoa com hanseníase','Pessoa com tuberculose','Sintomáticos Respiratórios','Tabagista',
    'Domiciliados / Acamados','Condições de vulnerabilidade social','Condicionalidade do Bolsa Família',
    'Saúde mental','Usuário de álcool','Usuário de outras drogas',
];

// --- HELPERS ---
const calculateAgesAndBadges = (dtNascimento) => {
    if (!dtNascimento) return { idade: '', isRN: false, isCrianca: false, isIdoso: false };
    const birth = new Date(dtNascimento);
    const today = new Date();
    const diffDays = Math.ceil(Math.abs(today - birth) / (1000 * 60 * 60 * 24));
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return { idade: age, isRN: diffDays <= 28, isCrianca: age < 10, isIdoso: age >= 60 };
};

const getAddressKey = (p) =>
    `${(p.endereco || 'ZZ').trim().toLowerCase()}|${(p.numero || 'SN').trim().toLowerCase()}|${(p.complemento || '').trim().toLowerCase()}`;

const formatAddressDisplay = (p) =>
    `${(p.endereco || 'Endereço n/d').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}${p.numero ? `, ${p.numero}` : ''}${p.complemento ? ` (${p.complemento})` : ''}`;

// Formata ISO "2002-05-16T00:00:00" → "16/05/2002" (para CSV / index.js)
const formatDnForCSV = (dtStr) => {
    if (!dtStr) return '';
    const d = new Date(dtStr);
    if (isNaN(d.getTime())) return '';
    return [
        String(d.getUTCDate()).padStart(2, '0'),
        String(d.getUTCMonth() + 1).padStart(2, '0'),
        d.getUTCFullYear(),
    ].join('/');
};

// Formata "yyyy-mm-dd" → "dd/mm/yyyy"  (data da visita no CSV)
const formatVisitDateForCSV = (dateStr) => {
    if (!dateStr) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [ano, mes, dia] = dateStr.split('-');
        return `${dia}/${mes}/${ano}`;
    }
    return dateStr; // já estava em outro formato, usa como está
};

// Constrói membro SLIM (apenas campos necessários) a partir de um paciente completo
// Constrói membro SLIM (apenas campos necessários) a partir de um paciente completo
const buildSlimMember = (p) => {
    const calc = calculateAgesAndBadges(p.dt_nascimento);
    
    // Dicionário de Tradução: Banco de Dados -> Rótulos do e-SUS/CSV
    const deParaCondicoes = {
        'GESTANTE': 'Gestante',
        'HIPERTENSAO': 'Pessoa com hipertensão',
        'DIABETES': 'Pessoa com diabetes',
        'ACAMADO': 'Domiciliados / Acamados',
        'DOMICILIADO': 'Domiciliados / Acamados',
        'TABAGISTA': 'Tabagista',
        'ALCOOLISMO': 'Usuário de álcool',
        'USO_DROGAS': 'Usuário de outras drogas',
        'CANCER': 'Pessoa com câncer',
        'TUBERCULOSE': 'Pessoa com tuberculose',
        'HANSENIASE': 'Pessoa com hanseníase',
        'SITUACAO_RUA': 'Condições de vulnerabilidade social',
        'SM': 'Saúde mental',
        // Condições que não têm botão específico no e-SUS caem em crônicas:
        'DOENCA_RENAL': 'Pessoa com outras doenças crônicas',
        'DOENCA_RESPIRATORIA': 'Pessoa com outras doenças crônicas', 
        'AVC': 'Pessoa com outras doenças crônicas',
        'INFARTO': 'Pessoa com outras doenças crônicas'
    };

    const iniciais = p.condicoes_fixas || (() => {
        const c = [];
        
        // 1. Traduz as condições que vieram do array do banco (se existir)
        if (p.condicoes_clinicas_array && Array.isArray(p.condicoes_clinicas_array)) {
            p.condicoes_clinicas_array.forEach(cond => {
                const labelFormatado = deParaCondicoes[cond];
                if (labelFormatado && !c.includes(labelFormatado)) {
                    c.push(labelFormatado);
                }
            });
        } else {
            // Fallback para as flags soltas (se vier da tela de Folha de Visita)
            if (p.is_hipertenso === true) c.push('Pessoa com hipertensão');
            if (p.is_diabetico === true) c.push('Pessoa com diabetes');
            if (p.is_acamado === true || p.is_domiciliado === true) c.push('Domiciliados / Acamados');
            if (p.is_gestante === true) c.push('Gestante');
        }

        // 2. Condições etárias calculadas automaticamente
        if (calc.isRN) c.push('Recém-nascido');
        else if (calc.isCrianca) c.push('Criança');
        if (calc.isIdoso) c.push('Idoso');
        
        return [...new Set(c)]; // Remove possíveis duplicatas
    })();

    return {
        id_sistema:      p.id_sistema || '',   
        nome:            p.nome,
        dt_nascimento:   p.dt_nascimento,
        isRN:            calc.isRN,
        isCrianca:       calc.isCrianca,
        isIdoso:         calc.isIdoso,
        isSelected:      p.isSelected !== undefined ? p.isSelected : true,
        isExpanded:      p.isExpanded || false,
        status_visita:   p.status_visita || 'Pendente',
        acoes_dinamicas: p.acoes_dinamicas || [],
        condicoes_fixas: iniciais,
        observacao:      p.observacao || '',
    };
};

// --- BADGES ---
const BADGE_MAP = [
    { cond: 'Recém-nascido',                               or: (m) => m.isRN,                     t: 'RN',  bg: '#dbeafe', color: '#1e3a8a' },
    { cond: 'Criança',                                     or: (m) => m.isCrianca && !m.isRN,     t: 'CR',  bg: '#f3e8ff', color: '#581c87' },
    { cond: 'Idoso',                                       or: (m) => m.isIdoso,                  t: 'ID',  bg: '#f1f5f9', color: '#475569' },
    { cond: 'Gestante',                                                                            t: 'GE',  bg: '#fce7f3', color: '#9d174d' },
    { cond: 'Puérpera',                                                                            t: 'PP',  bg: '#fce7f3', color: '#9d174d' },
    { cond: 'Pessoa com hipertensão',                                                              t: 'HA',  bg: '#e0f2fe', color: '#075985' },
    { cond: 'Pessoa com diabetes',                                                                 t: 'DIA', bg: '#fee2e2', color: '#991b1b' },
    { cond: 'Domiciliados / Acamados',                                                             t: 'AC',  bg: '#fef3c7', color: '#92400e' },
    { cond: 'Pessoa com câncer',                                                                   t: 'CA',  bg: '#fce7f3', color: '#be185d' },
    { cond: 'Pessoa com outras doenças crônicas',                                                  t: 'DC',  bg: '#f3f4f6', color: '#374151' },
    { cond: 'Pessoa em reabilitação ou com deficiência',                                           t: 'DEF', bg: '#e0e7ff', color: '#4338ca' },
    { cond: null, orMulti: ['Usuário de outras drogas','Usuário de álcool'],                       t: 'DP',  bg: '#ffedd5', color: '#c2410c' },
    { cond: null, orMulti: ['Pessoa com hanseníase','Pessoa com tuberculose'],                     t: 'DIC', bg: '#ccfbf1', color: '#0f766e' },
    { cond: 'Saúde mental',                                                                        t: 'SM',  bg: '#f5f3ff', color: '#6d28d9' },
    { cond: 'Condicionalidade do Bolsa Família',                                                   t: 'BF',  bg: '#dcfce7', color: '#15803d' },
];

const MemberBadges = ({ condicoes, isRN, isCrianca, isIdoso }) => {
    const conds = condicoes || [];
    const mock = { isRN, isCrianca, isIdoso };
    const badges = BADGE_MAP
        .filter(b => {
            if (b.cond && conds.includes(b.cond)) return true;
            if (b.or && b.or(mock)) return true;
            if (b.orMulti && b.orMulti.some(c => conds.includes(c))) return true;
            return false;
        })
        .filter((b, i, arr) => arr.findIndex(x => x.t === b.t) === i);
    if (badges.length === 0) return null;
    return (
        <div style={{ display: 'flex', gap: '2px', flexWrap: 'wrap', marginTop: '2px' }}>
            {badges.map(b => (
                <span key={b.t} style={{ fontSize: '0.58rem', background: b.bg, color: b.color, padding: '1px 4px', borderRadius: '3px', fontWeight: '700', letterSpacing: '0.03em' }}>
                    {b.t}
                </span>
            ))}
        </div>
    );
};

// --- STATUS CONFIG ---
const STATUS_CONFIG = {
    Realizada: { icon: CheckCircle2, color: '#059669', bg: '#d1fae5', border: '#6ee7b7', label: 'Realizada' },
    Ausente:   { icon: XCircle,      color: '#dc2626', bg: '#fee2e2', border: '#fca5a5', label: 'Ausente'   },
    Recusa:    { icon: Ban,          color: '#d97706', bg: '#fef3c7', border: '#fcd34d', label: 'Recusa'    },
    Pendente:  {                      color: '#94a3b8', bg: 'transparent', border: '#e2e8f0', label: 'Pendente' },
};

// ─── COMPONENTE PRINCIPAL ──────────────────────────────────────────────────────
export default function LancamentoVisita() {
    const location = useLocation();

    const [allPatients, setAllPatients] = useState([]);
    const [, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [visitList, setVisitList] = useState(() => {
        try { return JSON.parse(localStorage.getItem('lancamentoVisitData') || '[]'); }
        catch { return []; }
    });
    const [syncStatus, setSyncStatus] = useState('☁️ Salvo');
    const isInitialRender = useRef(true);
    const [dataGlobal, setDataGlobal] = useState(new Date().toISOString().split('T')[0]);
    const [turnoGlobal, setTurnoGlobal] = useState('Manhã');

    // Auto-import quando navegar da FolhaVisita com state { importFromFolha: true }
    useEffect(() => {
        if (location.state?.importFromFolha) {
            // Pequeno delay para garantir que allPatients não interfira no import
            const tid = setTimeout(() => importarDaFolha(), 200);
            return () => clearTimeout(tid);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ─── PUXAR DA NUVEM ────────────────────────────────────────────────────────
    const puxarDaNuvem = async () => {
        setSyncStatus('⏳ Baixando...');
        try {
            // CORRIGIDO: Removido o 'await' duplo e a crase sobrando
            const response = await apiFetch('/api/usuarios/rascunho');
            const data = await response.json();
            const rascunho = data.lancamento || [];
            if (rascunho.length > 0) {
                isInitialRender.current = true;
                setVisitList(rascunho);
                setSyncStatus('☁️ Rascunho restaurado!');
            } else {
                alert('Nenhum rascunho encontrado na nuvem para o seu usuário.');
                setSyncStatus('☁️ Nuvem vazia');
            }
        } catch (err) {
            console.error(err);
            setSyncStatus('⚠️ Erro ao baixar');
        }
    };

    // ─── IMPORTAR DA FOLHA DE VISITA ───────────────────────────────────────────
    const importarDaFolha = () => {
        const savedFolha = localStorage.getItem('currentVisitList');
        if (!savedFolha) { alert('Nenhuma Folha de Visita encontrada no navegador.'); return; }
        try {
            const folhaList = JSON.parse(savedFolha);
            if (!folhaList?.length) { alert('A sua Folha de Visita está vazia.'); return; }
            const newHouses = folhaList
                .map(house => ({
                    key: house.key,
                    addressDisplay: house.addressDisplay,
                    data_visita: dataGlobal,
                    turno: turnoGlobal,
                    members: (house.members || []).filter(m => m.isSelected).map(buildSlimMember),
                }))
                .filter(house => house.members.length > 0);
            setVisitList(prev => {
                const existingKeys = new Set(prev.map(h => h.key));
                const toAdd = newHouses.filter(h => !existingKeys.has(h.key));
                return [...prev, ...toAdd];
            });
        } catch (e) { console.error('Erro ao importar:', e); }
    };

    // ─── AUTOSAVE (localStorage + nuvem, debounce 3s) ──────────────────────────
    useEffect(() => {
        if (isInitialRender.current) { isInitialRender.current = false; return; }
        // visitList já é slim — salva diretamente
        localStorage.setItem('lancamentoVisitData', JSON.stringify(visitList));
        setSyncStatus('⏳ Alterações pendentes...');
        const timer = setTimeout(async () => {
            if (visitList.length === 0) { setSyncStatus('☁️ Salvo'); return; }
            setSyncStatus('⏳ Salvando na nuvem...');
            try {
                await apiFetch('/api/usuarios/rascunho', {
                    method: 'PUT',
                    body: JSON.stringify({ tipo: 'lancamento', dados: visitList }),
                });
                setSyncStatus('☁️ Salvo na nuvem');
            } catch {
                setSyncStatus('⚠️ Erro ao sincronizar');
            }
        }, 3000);
        return () => clearTimeout(timer);
    }, [visitList]);

    // ─── CARREGAR PACIENTES DA API ─────────────────────────────────────────────
    // ─── CARREGAR PACIENTES DA API ─────────────────────────────────────────────
    useEffect(() => {
        const fetchAllPatients = async () => {
            setLoading(true);
            try {
                // CORRIGIDO: Endpoint da V2 e removido await duplo
                const response = await apiFetch('/api/v2/pacientes');
                const result = await response.json();
                const data = (result.data || []).map(p => ({
                    ...p,
                    ...calculateAgesAndBadges(p.dt_nascimento),
                }));
                setAllPatients(data);
            } catch (error) { console.error('Erro:', error); }
            finally { setLoading(false); }
        };
        fetchAllPatients();
    }, []);
    // ─── BUSCA (permite duplicados — sem filtrar já adicionados) ──────────────
    const searchResults = useMemo(() => {
        if (searchTerm.trim().length < 3) return [];
        const lowerTerm = searchTerm.toLowerCase();
        const households = {};
        allPatients.forEach(p => {
            const key = getAddressKey(p);
            if (!households[key]) households[key] = [];
            households[key].push(p);
        });
        const results = [];
        Object.entries(households).forEach(([key, members]) => {
            const rep = members[0];
            const fullAddress = `${rep.endereco || ''} ${rep.numero || ''} ${rep.complemento || ''}`.toLowerCase();
            if (fullAddress.includes(lowerTerm) || members.some(m => m.nome.toLowerCase().includes(lowerTerm))) {
                const alreadyAdded = visitList.some(v => v.key === key || v.key.startsWith(key + '_'));
                results.push({
                    key,
                    addressDisplay: formatAddressDisplay(rep),
                    members: members.sort((a, b) => b.idade - a.idade),
                    alreadyAdded,
                });
            }
        });
        return results.slice(0, 8);
    }, [searchTerm, allPatients, visitList]);

    // ─── ADICIONAR DOMICÍLIO (SLIM, permite duplicatas) ────────────────────────
    const handleAddHouse = (house) => {
        const alreadyExists = visitList.some(v => v.key === house.key || v.key.startsWith(house.key + '_'));
        const uniqueKey = alreadyExists ? `${house.key}_${Date.now()}` : house.key;
        const newItem = {
            key:            uniqueKey,
            addressDisplay: house.addressDisplay,
            data_visita:    dataGlobal,
            turno:          turnoGlobal,
            members:        house.members.map(buildSlimMember),
        };
        setVisitList(prev => [...prev, newItem]);
        setSearchTerm('');
    };

    const handleRemoveHouse = (key) => setVisitList(prev => prev.filter(h => h.key !== key));

    // ─── DATA/TURNO POR DOMICÍLIO ──────────────────────────────────────────────
    const setHouseDate = (key, date) =>
        setVisitList(prev => prev.map(h => h.key === key ? { ...h, data_visita: date } : h));
    const setHouseTurno = (key, turno) =>
        setVisitList(prev => prev.map(h => h.key === key ? { ...h, turno } : h));
    const applyGlobalDateToAll = () =>
        setVisitList(prev => prev.map(h => ({ ...h, data_visita: dataGlobal, turno: turnoGlobal })));

    // ─── MUTAÇÕES DE MEMBROS ───────────────────────────────────────────────────
    const toggleSelection = (houseKey, id_sistema, isChecked) =>
        setVisitList(prev => prev.map(h => h.key !== houseKey ? h : {
            ...h, members: h.members.map(m => m.id_sistema === id_sistema ? { ...m, isSelected: isChecked } : m),
        }));

    const toggleAccordion = (houseKey, id_sistema) =>
        setVisitList(prev => prev.map(h => h.key !== houseKey ? h : {
            ...h, members: h.members.map(m => m.id_sistema === id_sistema ? { ...m, isExpanded: !m.isExpanded } : m),
        }));

    const setDesfecho = (houseKey, id_sistema, status) =>
        setVisitList(prev => prev.map(h => h.key !== houseKey ? h : {
            ...h, members: h.members.map(m => m.id_sistema !== id_sistema ? m : { ...m, status_visita: status }),
        }));

    const toggleArrayItem = (houseKey, id_sistema, fieldArray, value) =>
        setVisitList(prev => prev.map(h => {
            if (h.key !== houseKey) return h;
            return {
                ...h,
                members: h.members.map(m => {
                    if (m.id_sistema !== id_sistema) return m;
                    const list = m[fieldArray] || [];
                    const newList = list.includes(value) ? list.filter(i => i !== value) : [...list, value];
                    const newStatus = m.status_visita === 'Pendente' ? 'Realizada' : m.status_visita;
                    return { ...m, [fieldArray]: newList, status_visita: newStatus };
                }),
            };
        }));

    const handleObsChange = (houseKey, id_sistema, text) =>
        setVisitList(prev => prev.map(h => h.key !== houseKey ? h : {
            ...h, members: h.members.map(m => m.id_sistema === id_sistema ? { ...m, observacao: text } : m),
        }));

    const handleBulkCycle = (houseKey) =>
        setVisitList(prev => prev.map(house => {
            if (house.key !== houseKey) return house;
            const sel = house.members.filter(m => m.isSelected);
            if (!sel.length) return house;
            const allR = sel.every(m => m.status_visita === 'Realizada');
            const allA = sel.every(m => m.status_visita === 'Ausente');
            let next = 'Realizada';
            if (allR) next = 'Ausente';
            else if (allA) next = 'Pendente';
            return {
                ...house,
                members: house.members.map(m => {
                    if (!m.isSelected) return m;
                    if (next === 'Realizada') return { ...m, status_visita: 'Realizada', acoes_dinamicas: ['Periódica', 'Orientação / Prevenção'] };
                    if (next === 'Ausente') return { ...m, status_visita: 'Ausente', acoes_dinamicas: [] };
                    return { ...m, status_visita: 'Pendente', acoes_dinamicas: [], observacao: '' };
                }),
            };
        }));

    // ─── EXPORTAR CSV (formato compatível com index.js) ───────────────────────
    //  Colunas: NOME;DATA_NASCIMENTO;ID;DATA_VISITA;TURNO;DESFECHO;COMPARTILHADA;OBSERVACAO;MOTIVOS
    //  ID           = id_sistema (código numérico do Fastmedic, ex: "708421")
    //  TURNO        = 1=Manhã / 2=Tarde / 3=Noite
    //  DESFECHO     = 1=Realizada / 2=Ausente / 3=Recusa
    //  COMPARTILHADA= true/false  (baseado em "Visita compartilhada" nas ações)
    //  MOTIVOS      = CodMotivoVisitaDomiciliar de ACOES + CONDICOES, separados por vírgula
    const exportarParaCSV = () => {
        const esc = (v) => (v || '').toString().replace(/(\r\n|\n|\r)/gm, ' ');
        let rows = 'NOME;DATA_NASCIMENTO;ID;DATA_VISITA;TURNO;DESFECHO;COMPARTILHADA;OBSERVACAO;MOTIVOS\n';
        let exportados = 0;
        const semId = [];

        visitList.forEach(house => {
            const houseDate  = house.data_visita || dataGlobal;
            const houseTurno = house.turno       || turnoGlobal;
            const dataCSV    = formatVisitDateForCSV(houseDate);
            const turnoNum   = TURNO_MAP[houseTurno] ?? 1;

            house.members.forEach(m => {
                if (!m.isSelected || m.status_visita === 'Pendente') return;

                if (!m.id_sistema) {
                    semId.push(m.nome);
                    return;
                }

                const desfechoNum   = DESFECHO_MAP[m.status_visita] ?? 1;
                const acoes         = m.acoes_dinamicas || [];
                const condicoes     = m.condicoes_fixas || [];
                const compartilhada = acoes.includes('Visita compartilhada');

                // Junta ações + condições, remove duplicatas de código, ordena
                const todosLabels = [...acoes.filter(a => a !== 'Visita compartilhada'), ...condicoes];
                const motivoCodes = [...new Set(
                    todosLabels
                        .filter(label => MOTIVO_CODES[label] !== undefined)
                        .map(label => MOTIVO_CODES[label])
                )].sort((a, b) => a - b).join(', ');

                rows += [
                    esc(m.nome),
                    esc(formatDnForCSV(m.dt_nascimento)),
                    esc(m.id_sistema),
                    esc(dataCSV),
                    turnoNum,
                    desfechoNum,
                    compartilhada,
                    esc(m.observacao || ''),
                    esc(motivoCodes),
                ].join(';') + '\n';
                exportados++;
            });
        });

        if (semId.length > 0) {
            alert(`⚠️ ${semId.length} paciente(s) sem ID do sistema foram ignorados:\n${semId.slice(0, 5).join('\n')}${semId.length > 5 ? '\n...' : ''}\n\nVerifique se o campo id_sistema está presente no cadastro.`);
        }
        if (exportados === 0) { alert('Nenhuma visita finalizada para exportar.'); return; }

        const blob = new Blob(['\ufeff' + rows], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', `lancamento_visitas_${dataGlobal}.csv`);
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
    };

    // ─── TOTAIS ────────────────────────────────────────────────────────────────
    const totalVisitas    = visitList.reduce((a, h) => a + h.members.filter(m => m.isSelected && m.status_visita !== 'Pendente').length, 0);
    const totalRealizadas = visitList.reduce((a, h) => a + h.members.filter(m => m.isSelected && m.status_visita === 'Realizada').length, 0);
    const totalAusentes   = visitList.reduce((a, h) => a + h.members.filter(m => m.isSelected && (m.status_visita === 'Ausente' || m.status_visita === 'Recusa')).length, 0);

    const currentUserStr = localStorage.getItem('user');
    const currentUser = currentUserStr ? JSON.parse(currentUserStr) : null;
    const temPermissao = currentUser?.permissoes?.includes('beta_lancamento');
    
    // Se não tiver permissão, expulsa para o dashboard sem avisar!
    if (!temPermissao) return <Navigate to="/" replace />;
    
    // ─── RENDER ────────────────────────────────────────────────────────────────
    return (
        <div className="acs-app" style={{ minHeight: '100vh', background: '#f0f9f4', paddingBottom: '60px' }}>

            {/* ── HEADER ── */}
            <header style={{ background: 'linear-gradient(135deg, #065f46 0%, #047857 60%, #059669 100%)', padding: '20px 32px', boxShadow: '0 2px 20px rgba(5,150,105,0.3)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>

                    {/* TÍTULO */}
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ width: '32px', height: '32px', background: 'rgba(255,255,255,0.2)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Home size={18} color="white" />
                            </div>
                            <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: '800', color: 'white', letterSpacing: '-0.02em' }}>
                                Lançamento de Visitas
                            </h1>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', paddingLeft: '42px', marginTop: '4px' }}>
                            <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>Preencha e exporte o CSV para o Automator</p>
                            <div style={{ background: syncStatus.includes('Erro') ? '#fee2e2' : 'rgba(0,0,0,0.15)', color: syncStatus.includes('Erro') ? '#991b1b' : 'white', padding: '3px 10px', borderRadius: '12px', fontSize: '0.7rem', fontWeight: '600' }}>
                                {syncStatus}
                            </div>
                        </div>
                    </div>

                    {/* CONTROLES GLOBAIS */}
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>

                        {/* DATA PADRÃO */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Calendar size={11} /> Data padrão
                            </label>
                            <SmartDatePicker
                                value={dataGlobal}
                                onChange={setDataGlobal}
                                style={{ '--sdp-bg': 'rgba(255,255,255,0.15)', '--sdp-color': 'white' }}
                            />
                        </div>

                        {/* TURNO PADRÃO */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Clock size={11} /> Turno padrão
                            </label>
                            <select value={turnoGlobal} onChange={(e) => setTurnoGlobal(e.target.value)}
                                style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.12)', color: 'white', fontSize: '0.875rem', fontWeight: '600', outline: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                                <option value="Manhã" style={{ color: '#1e293b', background: 'white' }}>Manhã</option>
                                <option value="Tarde" style={{ color: '#1e293b', background: 'white' }}>Tarde</option>
                                <option value="Noite" style={{ color: '#1e293b', background: 'white' }}>Noite</option>
                            </select>
                        </div>

                        {/* APLICAR A TODOS */}
                        {visitList.length > 0 && (
                            <button onClick={applyGlobalDateToAll} title="Aplicar data/turno padrão a todos os domicílios"
                                style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.12)', color: 'white', fontSize: '0.8rem', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontFamily: 'inherit' }}>
                                <CopyCheck size={14} /> Aplicar a todos
                            </button>
                        )}

                        {/* EXPORTAR */}
                        <button className="export-btn" onClick={exportarParaCSV}
                            style={{ background: 'white', color: '#059669', border: 'none', padding: '9px 20px', borderRadius: '8px', fontWeight: '700', display: 'flex', gap: '8px', alignItems: 'center', cursor: 'pointer', fontSize: '0.875rem', fontFamily: 'inherit', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
                            <Download size={16} /> Baixar CSV
                            {totalVisitas > 0 && (
                                <span style={{ background: '#059669', color: 'white', borderRadius: '20px', padding: '1px 7px', fontSize: '0.7rem', fontWeight: '800' }}>{totalVisitas}</span>
                            )}
                        </button>
                    </div>
                </div>

                {/* STATS */}
                {visitList.length > 0 && (
                    <div style={{ display: 'flex', gap: '16px', marginTop: '14px', flexWrap: 'wrap' }}>
                        {[
                            { label: 'Imóveis', value: visitList.length, color: 'rgba(255,255,255,0.9)' },
                            { label: 'Realizadas', value: totalRealizadas, color: '#6ee7b7' },
                            { label: 'Ausentes/Recusa', value: totalAusentes, color: '#fca5a5' },
                        ].map(stat => (
                            <div key={stat.label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ fontSize: '1.1rem', fontWeight: '800', color: stat.color }}>{stat.value}</span>
                                <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', fontWeight: '500' }}>{stat.label}</span>
                                <span style={{ color: 'rgba(255,255,255,0.2)', marginLeft: '8px' }}>·</span>
                            </div>
                        ))}
                    </div>
                )}
            </header>

            {/* ── CORPO ── */}
            <div style={{ padding: '24px 24px 0' }}>

                {/* BUSCA */}
                <div style={{ position: 'relative', marginBottom: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', background: 'white', borderRadius: '12px', border: '2px solid #e2e8f0', padding: '12px 18px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                        <Search size={20} color="#94a3b8" style={{ flexShrink: 0 }} />
                        <input type="text" placeholder="Buscar por endereço ou nome do morador..."
                            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ border: 'none', outline: 'none', width: '100%', marginLeft: '12px', fontSize: '0.95rem', color: '#1e293b', background: 'transparent', fontFamily: 'inherit' }} />
                        {searchTerm.length > 0 && searchTerm.length < 3 && (
                            <span style={{ fontSize: '0.75rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>mín. 3 caracteres</span>
                        )}
                    </div>

                    {/* DROPDOWN RESULTADOS */}
                    {searchResults.length > 0 && (
                        <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', zIndex: 50, overflow: 'hidden' }}>
                            {searchResults.map((res, idx) => (
                                <div key={res.key} className="search-result-item" onClick={() => handleAddHouse(res)}
                                    style={{ padding: '12px 18px', cursor: 'pointer', borderBottom: idx < searchResults.length - 1 ? '1px solid #f1f5f9' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div style={{ width: '36px', height: '36px', background: '#f0fdf4', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            <Home size={16} color="#059669" />
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.9rem' }}>{res.addressDisplay}</div>
                                            <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '1px' }}>
                                                {res.members.length} {res.members.length === 1 ? 'morador' : 'moradores'}
                                                {res.alreadyAdded && <span style={{ marginLeft: '8px', color: '#f59e0b', fontWeight: '700' }}>· já na lista</span>}
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#059669', fontSize: '0.8rem', fontWeight: '600' }}>
                                        <PlusCircle size={18} /> {res.alreadyAdded ? 'Adicionar novamente' : 'Adicionar'}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* EMPTY STATE */}
                {visitList.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                        <div style={{ width: '64px', height: '64px', background: '#e2e8f0', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                            <Home size={28} color="#94a3b8" />
                        </div>
                        <p style={{ color: '#94a3b8', fontSize: '0.95rem', fontWeight: '500', margin: '0 0 20px 0' }}>Busque um endereço ou inicie o roteiro abaixo</p>
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button onClick={importarDaFolha} style={{ background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7', padding: '10px 20px', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px', fontFamily: 'inherit' }}>
                                📋 Importar da Folha de Visita
                            </button>
                            <button onClick={puxarDaNuvem} style={{ background: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd', padding: '10px 20px', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px', fontFamily: 'inherit' }}>
                                ☁️ Puxar Rascunho da Nuvem
                            </button>
                        </div>
                    </div>
                )}

                {/* CARDS DE IMÓVEL */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {visitList.map(house => {
                        const selM = house.members.filter(m => m.isSelected);
                        const allR = selM.length > 0 && selM.every(m => m.status_visita === 'Realizada');
                        const allA = selM.length > 0 && selM.every(m => m.status_visita === 'Ausente');
                        const realizadas = selM.filter(m => m.status_visita === 'Realizada').length;
                        const ausentes   = selM.filter(m => m.status_visita === 'Ausente' || m.status_visita === 'Recusa').length;
                        let bulkCfg = { text: 'Marcar Realizadas', bg: '#d1fae5', color: '#065f46', border: '#6ee7b7' };
                        if (allR) bulkCfg = { text: 'Mudar p/ Ausente', bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' };
                        else if (allA) bulkCfg = { text: 'Limpar Tudo', bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' };

                        return (
                            <div key={house.key} className="house-card" style={{ background: 'white', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 12px rgba(0,0,0,0.05)', overflow: 'hidden' }}>

                                {/* CABEÇALHO DO IMÓVEL */}
                                <div style={{ background: 'linear-gradient(to right, #f8fafc, #f1f5f9)', padding: '10px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                                        <div style={{ width: '30px', height: '30px', background: '#e0f2fe', borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            <Home size={14} color="#0369a1" />
                                        </div>
                                        <div style={{ minWidth: 0 }}>
                                            <span style={{ fontWeight: '700', color: '#1e293b', fontSize: '0.88rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>{house.addressDisplay}</span>
                                            <div style={{ display: 'flex', gap: '8px', marginTop: '1px' }}>
                                                {realizadas > 0 && <span style={{ fontSize: '0.68rem', color: '#059669', fontWeight: '600' }}>✓ {realizadas}</span>}
                                                {ausentes   > 0 && <span style={{ fontSize: '0.68rem', color: '#dc2626', fontWeight: '600' }}>✗ {ausentes}</span>}
                                            </div>
                                        </div>
                                    </div>

                                    {/* DATEPICKER POR DOMICÍLIO */}
                                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
                                        <SmartDatePicker
                                            value={house.data_visita || dataGlobal}
                                            onChange={(v) => setHouseDate(house.key, v)}
                                        />
                                        <select value={house.turno || turnoGlobal}
                                            onChange={(e) => setHouseTurno(house.key, e.target.value)}
                                            style={{ padding: '5px 8px', borderRadius: '6px', border: '1px solid #e2e8f0', background: 'white', fontSize: '0.75rem', color: '#475569', fontFamily: 'inherit', cursor: 'pointer' }}>
                                            <option value="Manhã">Manhã</option>
                                            <option value="Tarde">Tarde</option>
                                            <option value="Noite">Noite</option>
                                        </select>
                                    </div>

                                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
                                        <button onClick={() => handleBulkCycle(house.key)} className="pill-btn"
                                            style={{ background: bulkCfg.bg, color: bulkCfg.color, border: `1px solid ${bulkCfg.border}`, padding: '5px 12px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontFamily: 'inherit' }}>
                                            <Zap size={11} /> {bulkCfg.text}
                                        </button>
                                        <button onClick={() => handleRemoveHouse(house.key)}
                                            style={{ background: 'transparent', border: '1px solid #fee2e2', borderRadius: '7px', cursor: 'pointer', padding: '5px 7px', display: 'flex', alignItems: 'center', color: '#ef4444' }}>
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>

                                {/* MEMBROS */}
                                <div style={{ padding: '4px 0' }}>
                                    {house.members.map((member, mIdx) => {
                                        const isAusente = member.status_visita === 'Ausente' || member.status_visita === 'Recusa';
                                        const statusCfg = STATUS_CONFIG[member.status_visita] || STATUS_CONFIG.Pendente;

                                        return (
                                            <div key={member.id_sistema} style={{ borderBottom: mIdx < house.members.length - 1 ? '1px solid #f1f5f9' : 'none', opacity: !member.isSelected ? 0.45 : 1 }}>

                                                {/* ── LINHA ÚNICA ── */}
                                                <div className="member-row" style={{
                                                    display: 'flex', gap: '8px', alignItems: 'center',
                                                    flexWrap: 'nowrap',          // linha única
                                                    padding: '9px 16px',
                                                    background: isAusente ? '#fff5f5' : 'transparent',
                                                    borderLeft: `3px solid ${statusCfg.border || 'transparent'}`,
                                                    overflow: 'hidden',
                                                }}>
                                                    {/* CHECKBOX */}
                                                    <input type="checkbox" checked={member.isSelected}
                                                        onChange={(e) => toggleSelection(house.key, member.id_sistema, e.target.checked)}
                                                        style={{ width: '15px', height: '15px', accentColor: '#059669', cursor: 'pointer', flexShrink: 0 }} />

                                                    {/* NOME + BADGES — largura fixa */}
                                                    <div style={{ flex: '0 0 190px', minWidth: 0, overflow: 'hidden' }}>
                                                        <div className="member-name-text" style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.83rem', lineHeight: 1.2 }}>
                                                            {member.nome}
                                                        </div>
                                                        <MemberBadges condicoes={member.condicoes_fixas} isRN={member.isRN} isCrianca={member.isCrianca} isIdoso={member.isIdoso} />
                                                    </div>

                                                    {/* STATUS BUTTONS — largura fixa */}
                                                    <div style={{ display: 'flex', gap: '3px', flexShrink: 0 }}>
                                                        {[
                                                            { status: 'Realizada', icon: CheckCircle2, activeColor: '#059669', activeBg: '#d1fae5', activeBorder: '#6ee7b7' },
                                                            { status: 'Ausente',   icon: XCircle,      activeColor: '#dc2626', activeBg: '#fee2e2', activeBorder: '#fca5a5' },
                                                            { status: 'Recusa',    icon: Ban,          activeColor: '#d97706', activeBg: '#fef3c7', activeBorder: '#fcd34d' },
                                                        ].map(({ status, icon: Icon, activeColor, activeBg, activeBorder }) => {
                                                            const isActive = member.status_visita === status;
                                                            return (
                                                                <button key={status} className="status-btn"
                                                                    onClick={() => setDesfecho(house.key, member.id_sistema, status)}
                                                                    disabled={!member.isSelected} title={status}
                                                                    style={{ width: '28px', height: '28px', borderRadius: '7px', border: `1.5px solid ${isActive ? activeBorder : '#e2e8f0'}`, background: isActive ? activeBg : 'white', color: isActive ? activeColor : '#cbd5e1', cursor: member.isSelected ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                    <Icon size={14} />
                                                                </button>
                                                            );
                                                        })}
                                                    </div>

                                                    {/* AÇÕES DINÂMICAS — cresce, rola se necessário */}
                                                    <div style={{ flex: '1 1 0', display: 'flex', gap: '3px', flexWrap: 'wrap', alignItems: 'center', minWidth: 0, overflow: 'hidden' }}>
                                                        {ACOES_DINAMICAS.map(acao => {
                                                            const isActive = (member.acoes_dinamicas || []).includes(acao.id);
                                                            return (
                                                                <button key={acao.id} className="pill-btn" title={acao.id}
                                                                    onClick={() => toggleArrayItem(house.key, member.id_sistema, 'acoes_dinamicas', acao.id)}
                                                                    disabled={!member.isSelected}
                                                                    style={{ padding: '2px 8px', borderRadius: '20px', fontSize: '0.65rem', fontWeight: '700', border: `1.5px solid ${isActive ? acao.color + '80' : '#e2e8f0'}`, background: isActive ? acao.bg : 'white', color: isActive ? acao.color : '#94a3b8', cursor: !member.isSelected ? 'not-allowed' : 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                                                                    {acao.label}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>

                                                    {/* BOTÃO CONDIÇÕES */}
                                                    <button onClick={() => toggleAccordion(house.key, member.id_sistema)} disabled={!member.isSelected}
                                                        style={{ display: 'flex', alignItems: 'center', gap: '3px', padding: '4px 9px', background: member.isExpanded ? '#f0fdf4' : 'white', border: `1px solid ${member.isExpanded ? '#6ee7b7' : '#e2e8f0'}`, borderRadius: '7px', cursor: !member.isSelected ? 'not-allowed' : 'pointer', fontSize: '0.7rem', color: member.isExpanded ? '#059669' : '#64748b', fontWeight: '600', whiteSpace: 'nowrap', fontFamily: 'inherit', flexShrink: 0 }}>
                                                        Cond. {member.isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                                    </button>
                                                </div>

                                                {/* OBSERVAÇÃO */}
                                                <div style={{ padding: '0 16px 9px 44px' }}>
                                                    <input type="text" value={member.observacao || ''}
                                                        onChange={(e) => handleObsChange(house.key, member.id_sistema, e.target.value)}
                                                        placeholder={isAusente ? 'Motivo da ausência ou recusa...' : 'Observação da visita...'}
                                                        disabled={!member.isSelected}
                                                        style={{ width: '100%', padding: '6px 12px', borderRadius: '7px', border: '1px solid #e2e8f0', fontSize: '0.76rem', background: '#f8fafc', color: '#475569', outline: 'none', fontFamily: 'inherit' }} />
                                                </div>

                                                {/* ACORDEÃO CONDIÇÕES */}
                                                {member.isExpanded && (
                                                    <div style={{ padding: '12px 16px 14px 44px', background: '#f8fffe', borderTop: '1px dashed #d1fae5' }}>
                                                        <p style={{ margin: '0 0 8px 0', fontSize: '0.67rem', color: '#059669', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Condições de Saúde</p>
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                                            {CONDICOES_FIXAS.map(cond => {
                                                                const isChecked = (member.condicoes_fixas || []).includes(cond);
                                                                return (
                                                                    <button key={cond} className="pill-btn"
                                                                        onClick={() => toggleArrayItem(house.key, member.id_sistema, 'condicoes_fixas', cond)}
                                                                        style={{ padding: '3px 9px', borderRadius: '20px', fontSize: '0.7rem', fontWeight: '600', border: `1.5px solid ${isChecked ? '#059669' : '#e2e8f0'}`, background: isChecked ? '#d1fae5' : 'white', color: isChecked ? '#065f46' : '#64748b', cursor: 'pointer', fontFamily: 'inherit' }}>
                                                                        {isChecked ? '✓ ' : ''}{cond}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}